/**
 * @file FrameStep.tsx
 * @description Шаг «Кадр под блок» (phase19.16, партия SB120, этап 1;
 * uiux-sb120-stage1 §2.4 и §3, вопросы 4 и 8; данные db-design-sb120-stage1
 * §1). Один шаг на два места: фото фона секции (после выбора файла и по
 * «Кадр…») и элемент «Фото» в колонке («при загрузке фото адаптировать под
 * колонку» — рамка по доле колонки × высоте).
 *
 * ```
 * ┌ Кадр под блок ─────────────────── ┐   на телефоне — во весь экран
 * │ [ ПК | Телефон ]                  │
 * │ ┌─ рамка по пропорции места ──┐   │   тяните фото; стрелки — по 5 %
 * │ Масштаб / Пропорции ├──●──┤       │   (что есть у этого места)
 * │ ! Фото 900 px уже кадра на ПК …   │
 * │ Другое фото для телефона …        │   (фон, вкладка «Телефон»)
 * ├───────────────────────────────────┤
 * │ [Пропустить]          [Готово]    │   закреплено снизу, 56 px
 * └───────────────────────────────────┘
 * ```
 *
 * Файл не режется: кадр — точка фокуса и масштаб (у фона) или пропорции
 * (у элемента) для ПК и телефона. «Пропустить» — фокус по центру, масштаб 1;
 * закрыть окно — без изменений. Рамку и предупреждение даёт вызывающий.
 */

import { useState } from "react";

import { Button } from "../../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../ui/dialog";
import { NoticeStrip } from "../../../ui/NoticeStrip";
import { DeviceTabs } from "../fields/DeviceTabs";
import { FrameBox } from "./FrameBox";
import { FrameNumbers } from "./FrameNumbers";
import { PhonePhotoRow } from "./PhonePhotoRow";
import { skippedFrame, type FrameDraft } from "../../../../lib/site-builder/frame-draft";
import type { Device } from "../../../../lib/site-builder/look-size";

interface Props {
  open: boolean;
  /** Фото ПК; у телефона — своё фото черновика или это же. */
  mediaId: string;
  initial: FrameDraft;
  /** С какой вкладки открыть (после выбора фото телефона — «Телефон»). */
  device?: Device;
  /** Пропорции рамки места (ширина ÷ высота) по экрану и черновику. */
  aspectOf: (device: Device, draft: FrameDraft) => number;
  /** «Фото … уже кадра» по экрану и черновику; `null` — нет. */
  warningOf?: (device: Device, draft: FrameDraft) => string | null;
  /** Что можно менять числом: масштаб (фон) или пропорции (элемент «По фото»). */
  numbers: "zoom" | "aspect" | "none";
  /** «Другое фото для телефона…» (только фон): черновик уходит вызывающему, он откроет медиатеку. */
  onPickPhone?: (draft: FrameDraft) => void;
  onDone: (draft: FrameDraft) => void;
  onClose: () => void;
}

export function FrameStep({ open, onClose, ...rest }: Props) {
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      {/* Тело заново на каждое открытие: черновик — из текущего значения. */}
      {open ? <FrameStepBody {...rest} /> : null}
    </Dialog>
  );
}

function FrameStepBody({ mediaId, initial, device: start = "desktop", aspectOf, warningOf, numbers, onPickPhone, onDone }: Omit<Props, "open" | "onClose">) {
  const [device, setDevice] = useState<Device>(start);
  const [draft, setDraft] = useState<FrameDraft>(initial);
  const phone = device === "phone";
  const shown = phone ? (draft.mediaIdPhone ?? mediaId) : mediaId;
  const focus = phone ? (draft.focusPhone ?? draft.focus) : draft.focus;
  const zoom = numbers === "zoom" ? (phone ? (draft.zoomPhone ?? draft.zoom ?? 1) : (draft.zoom ?? 1)) : 1;
  const warning = warningOf?.(device, draft) ?? null;

  return (
    <DialogContent fullScreen className="flex flex-col sm:max-h-[90vh] sm:max-w-3xl">
      <DialogHeader>
        <DialogTitle>Кадр под блок</DialogTitle>
        <DialogDescription>Файл не режется: двигайте фото в рамке — так блок покажет его на {phone ? "телефоне" : "ПК"}.</DialogDescription>
      </DialogHeader>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        <DeviceTabs value={device} onChange={setDevice} label="Кадр: экран" phoneOwn={draft.focusPhone !== undefined || draft.mediaIdPhone !== undefined || draft.aspectPhone !== undefined} />
        <FrameBox
          mediaId={shown}
          aspect={aspectOf(device, draft)}
          focus={focus}
          zoom={zoom}
          label={phone ? "Кадр телефона" : "Кадр ПК"}
          onFocus={(next) => setDraft(phone ? { ...draft, focusPhone: next } : { ...draft, focus: next })}
        />
        <FrameNumbers numbers={numbers} device={device} draft={draft} onChange={setDraft} />
        {warning ? <NoticeStrip tone="warning">{warning}</NoticeStrip> : null}
        {phone && onPickPhone ? (
          <PhonePhotoRow
            mediaIdPhone={draft.mediaIdPhone}
            onPick={() => onPickPhone(draft)}
            onRemove={() => {
              const next = { ...draft };
              delete next.mediaIdPhone;
              setDraft(next);
            }}
          />
        ) : null}
      </div>
      {/* На телефоне — закреплено у низа экрана; с 640 — обычный подвал окна (QA D5). */}
      <div className="flex shrink-0 gap-2 bg-surface-lowest max-sm:sticky max-sm:bottom-0 max-sm:-mx-6 max-sm:-mb-6 max-sm:px-6 max-sm:py-3 max-sm:[box-shadow:0_-1px_0_var(--outline-variant)] sm:justify-end">
        <Button variant="outline" className="h-14 flex-1 sm:mr-auto sm:flex-none" onClick={() => onDone(skippedFrame(draft))}>
          Пропустить
        </Button>
        <Button className="h-14 flex-1 sm:flex-none" onClick={() => onDone(draft)}>
          Готово
        </Button>
      </div>
    </DialogContent>
  );
}
